<script setup lang="ts">
  import { useLocale } from '@tundr/i18n';
  import { computed, ref, watch } from 'vue';
  import type { IconDropdownOption } from '../../IconDropdown/IconDropdown.types';
  import IconDropdown from '../../IconDropdown/IconDropdown.vue';
  import SearchInput from '../SearchInput/SearchInput.vue';
  import type {
    SearchInputWithSelectEmits,
    SearchInputWithSelectProps,
  } from './SearchInputWithSelect.types';

  const props = withDefaults(defineProps<SearchInputWithSelectProps>(), {
    modelValue: '',
    clearable: false,
    disabled: false,
    autofocus: false,
    disableTeleport: false,
  });

  const emit = defineEmits<SearchInputWithSelectEmits>();

  const { t } = useLocale('ds-components');

  const dropdownOptions = computed<IconDropdownOption[]>(() =>
    props.options.map(({ fieldName, label, icon }) => ({
      value: fieldName,
      label,
      icon,
    })),
  );

  const findOption = (value?: string | null) =>
    props.options.find((option) => option.fieldName === value);

  const selectedValue = ref<string | null>(
    findOption(props.selectedOption)?.fieldName ??
      (props.options.find((option) => option.default) ?? props.options[0])
        ?.fieldName ??
      null,
  );

  watch(
    () => props.selectedOption,
    (value) => {
      const option = findOption(value);
      if (option) {
        selectedValue.value = option.fieldName;
      }
    },
  );

  const searchByLabel = computed(() => {
    const label = findOption(selectedValue.value)?.label;
    return label
      ? `${t('search_input_with_select.search_by')} ${label.toLocaleLowerCase()}`
      : undefined;
  });

  const placeholderText = computed(
    () =>
      props.placeholder ?? searchByLabel.value ?? t('search_input.placeholder'),
  );

  const selectAriaLabel = computed(
    () => searchByLabel.value ?? t('search_input_with_select.search_by'),
  );

  const onSelect = (value: string) => {
    if (value === selectedValue.value) {
      return;
    }
    selectedValue.value = value;
    emit('update:selectedOption', value);
  };
</script>

<template>
  <SearchInput
    :model-value="modelValue"
    :placeholder="placeholderText"
    :name="name"
    :test-id="testId"
    :disabled="disabled"
    :clearable="clearable"
    :autofocus="autofocus"
    :size="size"
    @update:model-value="(value) => emit('update:modelValue', value)"
    @blur="(event) => emit('blur', event)"
  >
    <template #leading>
      <IconDropdown
        :model-value="selectedValue"
        :options="dropdownOptions"
        :disabled="disabled"
        :disable-teleport="disableTeleport"
        :aria-label="selectAriaLabel"
        :test-id="testId ? `${testId}-select` : undefined"
        @update:model-value="onSelect"
      />
    </template>
  </SearchInput>
</template>
